import {
  extractPayloadMessage,
  getFetchErrorData,
  getFetchErrorStatus,
  getQuotaErrorInfo,
  type QuotaErrorInfo,
} from './apiErrors'

export type OptionalTranslate = (
  key: string,
  params?: Record<string, unknown>
) => string | null

const QUOTA_MESSAGE_KEYS: Record<string, string> = {
  glossary_count: 'notifications.quota_glossary_count',
  glossary_entries: 'notifications.quota_glossary_entries',
  memory_count: 'notifications.quota_memory_count',
  memory_entries: 'notifications.quota_memory_entries',
}

// lmt-api-glossary and lmt-api-vector-search answered quota refusals with plain text only, before
// they added the structured `limit` / `current` / `quota_type` fields (LAN-394). Until that is
// deployed everywhere, recognise their four known messages so they read the same way, e.g.:
//   "Failed adding new glossaries. The maximum number of stored glossaries cannot exceed 5 (currently have 5)."
//   "Failed adding new entries. The maximum number of entries in this glossary cannot exceed 50 (currently have 50)."
//   "Failed adding new translation memories. The maximum number of stored translation memories cannot exceed 5 (currently have 5)."
//   "Failed adding new entries. The maximum number of entries in this translation memory cannot exceed 50 (currently have 50)."
const LEGACY_QUOTA =
  /maximum number of (stored glossaries|entries in this glossary|stored translation memories|entries in this translation memory) cannot exceed (\d+) \(currently have (\d+)\)/

const LEGACY_QUOTA_TYPES: Record<string, string> = {
  'stored glossaries': 'glossary_count',
  'entries in this glossary': 'glossary_entries',
  'stored translation memories': 'memory_count',
  'entries in this translation memory': 'memory_entries',
}

function getLegacyQuotaErrorInfo(response: unknown): QuotaErrorInfo | null {
  const error = { response }
  if (getFetchErrorStatus(error) !== 403) {
    return null
  }

  const detail = extractPayloadMessage(getFetchErrorData(error))
  const match = LEGACY_QUOTA.exec(detail)
  if (!match) {
    return null
  }

  return {
    quotaType: LEGACY_QUOTA_TYPES[match[1]],
    limit: Number(match[2]),
    current: Number(match[3]),
    detail,
  }
}

export interface QuotaToast {
  title: string
  description: string
}

// A response that refused the action because a plan quota is used up is not a failure the user
// can do anything wrong about, so it gets an informational toast with a plain-language message
// instead of the generic error toast. Returns null for every other response.
//
// Recognises the structured 403 body (`limit` / `current` / `quota_type`) and the older plain-text
// refusal; a plain permission 403 keeps the normal error toast.
export function getQuotaRefusal(response: unknown): QuotaErrorInfo | null {
  return getQuotaErrorInfo({ response }) ?? getLegacyQuotaErrorInfo(response)
}

// Whether the response is a refusal because a plan quota is used up -- for callers that would
// otherwise stack their own generic error toast on top of the informational one.
export function isQuotaRefusal(response: unknown): boolean {
  return getQuotaRefusal(response) !== null
}

export function buildQuotaToast(
  response: unknown,
  translate: OptionalTranslate
): QuotaToast | null {
  const info = getQuotaRefusal(response)
  if (!info) {
    return null
  }

  const messageKey = QUOTA_MESSAGE_KEYS[info.quotaType]
  const message = messageKey
    ? translate(messageKey, { limit: info.limit, current: info.current })
    : null

  return {
    title: translate('notifications.quota_title') || 'Plan limit reached',
    // An unknown quota type, or a missing translation, still says what the server said.
    description: message || info.detail,
  }
}
